import differenceBy from "lodash/differenceBy";
import { GenericError } from "../../../flux_error";
import { addPcbNodes, isDescendantOfLayout } from "../../../nodes";
import { getAncestorElementData } from "../helpers";
import FootprintVirtualNodeGenerator from "./generators/footprint/FootprintVirtualNodeGenerator";
import { getExistingPadNodes } from "./generators/footprint/helpers/getExistingPadNodes";
import { getFootprintAsset } from "./generators/footprint/helpers/getFootprintAsset";
// TODO it appears this class is now only used for footprint nodes.
// We should remove this extra abstraction layer and fold it into FootprintVirtualNodeGenerator.
export default class PcbLayoutVirtualNodeGenerator {
    elements;
    assets;
    treeManager;
    runtimeRulesManager;
    logger;
    kicadFootprintToFluxFootprint;
    constructor(elements, assets, treeManager, runtimeRulesManager, logger, kicadFootprintToFluxFootprint) {
        this.elements = elements;
        this.assets = assets;
        this.treeManager = treeManager;
        this.runtimeRulesManager = runtimeRulesManager;
        this.logger = logger;
        this.kicadFootprintToFluxFootprint = kicadFootprintToFluxFootprint;
    }
    /**
     * NOTE: this will replace virtual nodes that originate from the same node that is passed in.
     *
     * Only generate virtual nodes from footprint nodes' asset rule
     */
    generateVirtualNodesFromRules(footprintNode, footprintBakedRules) {
        // First, clear out existing.
        // Typescript weird error here TODO fix it
        // @ts-expect-error
        const nodesToRemove = this.treeManager.removeVirtualNodesOfFootprintAsset(footprintNode);
        const ruleSetsToRemove = this.runtimeRulesManager.deleteRuntimePcbLayoutRulesFromAncestor(footprintNode);
        // computing asset rule is expensive...so lets only do it for active nodes
        if (!footprintBakedRules.active || !(footprintBakedRules.asset ?? footprintBakedRules.package)) {
            return {
                nodesToRemove,
                ruleSetsToRemove,
                nodesToAdd: [],
                ruleSetsToAdd: {},
            };
        }
        const virtualObjects = this.generateVirtualObjects(footprintNode, footprintBakedRules);
        if (!virtualObjects) {
            return {
                nodesToRemove,
                ruleSetsToRemove,
                nodesToAdd: [],
                ruleSetsToAdd: {},
            };
        }
        const nodesToAdd = [];
        addPcbNodes(this.treeManager.treeNodes, virtualObjects.virtualNodes);
        nodesToAdd.push(...virtualObjects.virtualNodes);
        // NOTE: log only the first instance to avoid flooding the logs
        let errorSeen = false;
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.values(virtualObjects.virtualRuleSets).forEach((ruleSet) => {
            // This is a sanity check. See getRuleSetsForNode for usage.
            if (!this.treeManager.treeNodes[ruleSet.uid] && !errorSeen) {
                this.logger.captureError(new GenericError(`Ruleset ${ruleSet.uid} does not have a corresponding node. Only the first miss logged.`));
                errorSeen = true;
            }
        });
        // QUESTION: why not are runtime rules not just named virtualRuleSets?
        this.runtimeRulesManager.addRuntimePcbLayoutRules(virtualObjects.virtualRuleSets);
        return {
            // NOTE: differenceBy is an optimization to prevent more writes downstream
            nodesToRemove: differenceBy(nodesToRemove, nodesToAdd, (node) => node.uid),
            ruleSetsToRemove: ruleSetsToRemove.filter((uid) => !virtualObjects.virtualRuleSets[uid]),
            nodesToAdd,
            ruleSetsToAdd: virtualObjects.virtualRuleSets,
        };
    }
    generateVirtualObjects(footprintNode, footprintBakedRules) {
        const footprintVirtualNodeGenerator = new FootprintVirtualNodeGenerator(this.logger);
        const footprintAncestorElementData = getAncestorElementData(footprintNode, this.treeManager.treeNodes, this.elements, this.logger);
        const footprintAsset = getFootprintAsset(footprintBakedRules, this.assets);
        // NOTE: When there's no `existingPadNodes`, we would still generate virtual pad nodes for the footprint and
        // put them under `Mounts` folder
        const existingPadNodes = getExistingPadNodes(footprintNode, this.treeManager.treeNodes, this.logger) || [];
        // Distinguish between whether we're generating virtual nodes for a top-level footprint in the current
        // document (typically a primitive part) vs. a for a footprint that is in a submodule of a host project.
        const isInSubmodule = isDescendantOfLayout(this.treeManager.treeNodes, footprintNode.uid);
        return footprintVirtualNodeGenerator.generate(footprintNode.uid, footprintBakedRules, footprintAncestorElementData, footprintAsset, this.elements, existingPadNodes, isInSubmodule, this.kicadFootprintToFluxFootprint);
    }
}
//# sourceMappingURL=PcbLayoutVirtualNodeGenerator.js.map